import axios from 'axios';
import { error, log as logDiag } from '@/utils/logger';

const API_URL = process.env.NEXT_PUBLIC_APP_API_BASE_URL;

const BASE = `${API_URL}/api/v2/advisor-pro`;

export const getAuthHeaders = () => {
  const rawAccessToken = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;

  if (!rawAccessToken) {
    error('[advisorPro:getAuthHeaders] No access token found in localStorage');
    throw new Error('Authentication token not found. User must be logged in.');
  }

  const accessToken = rawAccessToken.startsWith('Bearer ')
    ? rawAccessToken.substring(7)
    : rawAccessToken;

  const headers: Record<string, string> = {
    Authorization: accessToken,
  };

  const rawIdToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : null;
  if (rawIdToken) {
    const idToken = rawIdToken.startsWith('Bearer ') ? rawIdToken.substring(7) : rawIdToken;
    headers['x-id-token'] = idToken;
  }

  return headers;
};

export type AdvisorProStatusValue =
  | 'none'
  | 'pending_payment'
  | 'active'
  | 'past_due'
  | 'canceled';

export type AdvisorProPlan = 'monthly' | 'annual';

export type CredentialType = 'iata' | 'iatan';

export type VerificationStatus = 'unsubmitted' | 'pending' | 'verified' | 'rejected';

export interface AdvisorProOnboardingFlags {
  credentials_submitted: boolean;
  services_configured: boolean;
  connect_started: boolean;
  connect_completed: boolean;
}

export interface AdvisorProState {
  status: AdvisorProStatusValue;
  plan: AdvisorProPlan | null;
  upgraded_at: string | null;
  canceled_at: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  verification_status: VerificationStatus;
  credential_type: CredentialType | null;
  connect_charges_enabled: boolean;
  connect_payouts_enabled: boolean;
  is_pro_active: boolean;
  can_offer_paid_services: boolean;
  can_sell_to_travelers: boolean;
  can_start_checkout: boolean;
  can_open_billing_portal: boolean;
  onboarding: AdvisorProOnboardingFlags;
}

export interface AdvisorProStatusResponse {
  profile_type: string;
  advisor_pro: AdvisorProState;
}

export interface CheckoutSessionResponse {
  url: string;
  session_id: string;
}

export interface AdvisorProPlanMetadata {
  plan: AdvisorProPlan;
  price_id: string;
  unit_amount: number;
  unit_amount_display: string;
  currency: string;
  interval: 'month' | 'year';
  interval_count: number;
  active: boolean;
}

export interface AdvisorProPlansResponse {
  plans: AdvisorProPlanMetadata[];
  promotion?: {
    code: string | null;
    applies_to_plan: AdvisorProPlan;
  };
}

export interface BillingPortalResponse {
  url: string;
}

export interface CredentialsPayload {
  credential_type: CredentialType;
  credential_number: string;
  iata_number: string;
}

export type ServicePricingMode = 'free' | 'paid';

/** How a paid service's `price_amount` is charged; `null` on services saved before it existed. */
export type ServicePriceUnit = 'flat' | 'per_day';

export interface AdvisorProService {
  key: string;
  title: string;
  description?: string;
  pricing_mode: ServicePricingMode;
  price_amount?: number;
  currency?: string;
  price_unit?: ServicePriceUnit | null;
  duration_minutes?: number;
  enabled: boolean;
}

export interface ConnectStatusResponse {
  account_id: string | null;
  verification_status: VerificationStatus;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  details_submitted: boolean;
}

export interface AdvisorProVerificationQueueItem {
  _id: string;
  name?: string;
  email?: string;
  profile_type?: string;
  agency_name?: string | null;
  iata_number?: string | null;
  advisor_pro?: {
    status?: AdvisorProStatusValue;
    verification_status?: VerificationStatus;
    credential_type?: CredentialType | null;
    credential_number?: string | null;
    subscription_type?: 'Monthly' | 'Annual' | null;
    verification_review_notes?: string | null;
    updated_at?: string | null;
  };
}

export interface AdvisorProVerificationQueueResponse {
  users: AdvisorProVerificationQueueItem[];
  status: VerificationStatus;
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

export interface SetAdvisorProVerificationPayload {
  status: 'verified' | 'rejected';
  notes?: string;
}

export interface SetAdvisorProVerificationResponse {
  user_id: string;
  verification_status: VerificationStatus;
  reviewed_at: string | null;
  reviewed_by: string | null;
}

export interface RevokeAdvisorProPayload {
  reason: string;
  refund?: boolean;
}

export interface RevokeAdvisorProResponse {
  user_id: string;
  status: AdvisorProStatusValue;
  canceled_at: string | null;
  refund_requested: boolean;
}

/** Backend wraps some v2 responses in `{ data: ... }`; unwrap when present. */
const unwrap = <T,>(payload: unknown): T =>
  ((payload as { data?: T })?.data ?? payload) as T;

export async function getAdvisorProStatus(options?: {
  signal?: AbortSignal;
}): Promise<AdvisorProStatusResponse> {
  const url = `${BASE}/status`;

  try {
    const response = await axios.get(url, {
      headers: getAuthHeaders(),
      signal: options?.signal,
      skipAuthErrorHandling: true,
    });
    const payload = unwrap<AdvisorProStatusResponse>(response.data);

    // Temporary diagnostic: the authoritative Advisor Pro state for the caller.
    const advisorPro = payload?.advisor_pro as unknown as Record<string, unknown> | undefined;
    logDiag('[AdvisorProStatus] GET', {
      request_url: url,
      status_code: response.status,
      profile_type: payload?.profile_type,
      advisor_pro: advisorPro,
      advisor_pro_keys: advisorPro ? Object.keys(advisorPro).sort() : [],
      status: advisorPro?.status,
      is_pro_active: advisorPro?.is_pro_active,
      verification_status: advisorPro?.verification_status,
      can_start_checkout: advisorPro?.can_start_checkout,
      can_open_billing_portal: advisorPro?.can_open_billing_portal,
      onboarding: advisorPro?.onboarding,
      raw: response.data,
    });

    return payload;
  } catch (err) {
    const status = axios.isAxiosError(err) ? err.response?.status : undefined;
    logDiag('[AdvisorProStatus] FAILED', {
      request_url: url,
      status_code: status,
      is_canceled: axios.isCancel(err),
      response_body: axios.isAxiosError(err) ? err.response?.data : undefined,
    });

    error('[getAdvisorProStatus] Error fetching Advisor Pro status:', err);
    throw err;
  }
}

export async function getAdvisorProPlans(options?: {
  signal?: AbortSignal;
}): Promise<AdvisorProPlansResponse> {
  try {
    const response = await axios.get(`${BASE}/plans`, {
      headers: getAuthHeaders(),
      signal: options?.signal,
      skipAuthErrorHandling: true,
    });
    return unwrap<AdvisorProPlansResponse>(response.data);
  } catch (err) {
    error('[getAdvisorProPlans] Error fetching Advisor Pro plans:', err);
    throw err;
  }
}

export async function createCheckoutSession(params: {
  plan: AdvisorProPlan;
  successUrl: string;
  cancelUrl: string;
}): Promise<CheckoutSessionResponse> {
  try {
    const response = await axios.post(
      `${BASE}/checkout-session`,
      {
        plan: params.plan,
        success_url: params.successUrl,
        cancel_url: params.cancelUrl,
      },
      { headers: getAuthHeaders() }
    );
    return unwrap<CheckoutSessionResponse>(response.data);
  } catch (err) {
    error('[createCheckoutSession] Error creating checkout session:', err);
    throw err;
  }
}

export async function createBillingPortalSession(
  returnUrl: string
): Promise<BillingPortalResponse> {
  try {
    const response = await axios.post(
      `${BASE}/billing-portal`,
      { return_url: returnUrl },
      { headers: getAuthHeaders() }
    );
    return unwrap<BillingPortalResponse>(response.data);
  } catch (err) {
    error('[createBillingPortalSession] Error creating billing portal session:', err);
    throw err;
  }
}

export async function putAdvisorProCredentials(
  payload: CredentialsPayload
): Promise<void> {
  try {
    await axios.put(`${BASE}/credentials`, payload, { headers: getAuthHeaders() });
  } catch (err) {
    error('[putAdvisorProCredentials] Error submitting credentials:', err);
    throw err;
  }
}

export async function putAdvisorProServices(
  services: AdvisorProService[]
): Promise<void> {
  try {
    await axios.put(`${BASE}/services`, { services }, { headers: getAuthHeaders() });
  } catch (err) {
    error('[putAdvisorProServices] Error saving services:', err);
    throw err;
  }
}

export async function createConnectAccountLink(params: {
  refreshUrl: string;
  returnUrl: string;
}): Promise<{ url: string }> {
  try {
    const response = await axios.post(
      `${BASE}/connect/account-link`,
      { refresh_url: params.refreshUrl, return_url: params.returnUrl },
      { headers: getAuthHeaders() }
    );
    return unwrap<{ url: string }>(response.data);
  } catch (err) {
    error('[createConnectAccountLink] Error creating Connect account link:', err);
    throw err;
  }
}

export async function getConnectStatus(options?: {
  signal?: AbortSignal;
}): Promise<ConnectStatusResponse> {
  try {
    const response = await axios.get(`${BASE}/connect/status`, {
      headers: getAuthHeaders(),
      signal: options?.signal,
      skipAuthErrorHandling: true,
    });
    return unwrap<ConnectStatusResponse>(response.data);
  } catch (err) {
    error('[getConnectStatus] Error fetching Connect status:', err);
    throw err;
  }
}

export async function getAdvisorProVerificationQueue(params?: {
  status?: VerificationStatus;
  page?: number;
  limit?: number;
  signal?: AbortSignal;
}): Promise<AdvisorProVerificationQueueResponse> {
  try {
    const response = await axios.get(
      `${API_URL}/api/v2/admin/advisor-pro/verifications`,
      {
        headers: getAuthHeaders(),
        params: {
          status: params?.status,
          page: params?.page,
          limit: params?.limit,
        },
        signal: params?.signal,
      }
    );
    return unwrap<AdvisorProVerificationQueueResponse>(response.data);
  } catch (err) {
    error('[getAdvisorProVerificationQueue] Error fetching Advisor Pro verification queue:', err);
    throw err;
  }
}

export async function setAdvisorProVerificationStatus(
  userId: string,
  payload: SetAdvisorProVerificationPayload
): Promise<SetAdvisorProVerificationResponse> {
  try {
    const response = await axios.post(
      `${API_URL}/api/v2/admin/advisor-pro/${userId}/verification`,
      payload,
      { headers: getAuthHeaders() }
    );
    return unwrap<SetAdvisorProVerificationResponse>(response.data);
  } catch (err) {
    error('[setAdvisorProVerificationStatus] Error updating Advisor Pro verification status:', err);
    throw err;
  }
}

export async function revokeAdvisorPro(
  userId: string,
  payload: RevokeAdvisorProPayload
): Promise<RevokeAdvisorProResponse> {
  try {
    const response = await axios.post(
      `${API_URL}/api/v2/admin/advisor-pro/${userId}/revoke`,
      payload,
      { headers: getAuthHeaders() }
    );
    return unwrap<RevokeAdvisorProResponse>(response.data);
  } catch (err) {
    error('[revokeAdvisorPro] Error revoking Advisor Pro:', err);
    throw err;
  }
}
